import React from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';

export default function UnderstandingInstantFlows() {
  // Market growth chart data
  const marketGrowthData = [
    { year: '2022', value: 124 },
    { year: '2027 (proj.)', value: 297 }
  ];

  // Variance chart data
  const varianceData = [
    { name: 'Human Panel', value: 1.8, description: 'Wider range of opinions' },
    { name: 'AI-Generated Panel', value: 1.1, description: 'More clustered, "average" opinions' }
  ];

  return (
    <div className="blog-content">
      <p>In the rapidly evolving field of AI-powered market research, questions about reliability and transparency are essential. At InstantFlows, we believe in being forthright about both the capabilities and limitations of our technology. This post explains how our system works, where our results come from, and what makes them reliable enough for your business decisions.</p>

      <div className="chart-section" style={{ height: 400 }}>
        <h2>The Market Disruption</h2>
        <p>
          The AI software market is projected to surge from $124B in 2022 to nearly $300B by 2027. Generative AI is the driving force, shifting market research from a slow, episodic process to a continuous source of strategic advantage.
        </p>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={marketGrowthData}>
            <XAxis dataKey="year" />
            <YAxis label={{ value: 'Billion USD', angle: -90, position: 'insideLeft' }} />
            <Tooltip formatter={(value) => [`$${value}B`, 'Spending']} />
            <Legend />
            <Bar dataKey="value" name="Global AI Software Spending" fill="#3b82f6" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <h2>The Technology Behind InstantFlows</h2>
      <p>Our platform uses OpenAI's GPT models (currently GPT-4) to generate survey responses from virtual personas, integrated into a serverless AWS architecture using Lambda functions, API Gateway, DynamoDB, and SQS queues to manage the research workflow.</p>
      
      <p>When you create a research project, here's what happens behind the scenes:</p>

      <div className="process-section">
        {[
          {
            title: "Step 1: Survey Generation",
            description: "The process begins when a user provides a research description. The system uses a Large Language Model (LLM) to generate relevant, structured survey questions, leveraging sophisticated prompt engineering.",
            strengths: "Uses structured prompts and schema validation to ensure usable survey formats.",
            limitations: "Quality is dependent on the LLM and currently lacks a 'Survey Quality Validation' step."
          },
          {
            title: "Step 2: Persona Creation",
            description: "To create a realistic virtual panel, the system samples from demographic data derived from national publicly available databases such as CBS (Centraal Bureau voor de Statistiek) and other authoritative sources, ensuring the overall panel is demographically representative.",
            strengths: "Grounded in real demographic data, providing a statistically defensible basis for representativeness.",
            limitations: "Suffers from the 'psychographic gap'—personas lack deeper values and interests."
          },
          {
            title: "Step 3: Response Generation",
            description: "The LLM is asked to 'roleplay' as each specific persona and answer each survey question, using the persona's full demographic profile. The LLM leverages patterns it has observed in its training data about how different demographic groups tend to respond to various questions. While the system doesn't have direct access to specific statistics for every demographic group, it has been trained on millions of documents that contain correlations between demographic factors and various behaviors and preferences. We're continuously working to enrich our personas with more specific behavioral data from public research studies, allowing for even more precise response generation based on documented demographic patterns rather than general correlations.",
            strengths: "Context-aware prompts significantly improve response quality.",
            limitations: "Responses can be inconsistent across questions due to the LLM's reasoning constraints."
          },
          {
            title: "Step 4: Analysis",
            description: "Once responses are collected, another LLM-driven process analyzes the results, identifying patterns and providing demographic breakdowns.",
            strengths: "Extremely fast and provides structured, consistent output formats.",
            limitations: "Risks 'insight hallucinations' and lacks traditional statistical rigor."
          }
        ].map(({ title, description, strengths, limitations }) => (
          <div key={title} className="card">
            <h3>{title}</h3>
            <p>{description}</p>
            <div className="strength">
              <h5>Strength</h5>
              <p>{strengths}</p>
            </div>
            <div className="limitation">
              <h5>Limitation</h5>
              <p>{limitations}</p>
            </div>
          </div>
        ))}
      </div>

      <h2>What Makes Our Results Reliable?</h2>

      <div className="strengths-section">
        <h3>Strengths of Our Approach</h3>
        <div className="content-grid-2">
          {[
            { title: "Demographic Representation", description: "By basing our personas on real demographic distributions from authoritative sources, we ensure a statistically representative sample of virtual respondents." },
            { title: "Validation Mechanisms", description: "Our system includes multiple validation checks to ensure responses align with known demographic patterns and maintain internal consistency." },
            { title: "Continuous Benchmarking", description: "We regularly compare our synthetic responses against results from traditional research studies to calibrate our system and improve accuracy." },
            { title: "Transparent Limitations", description: "Unlike black-box solutions, we clearly communicate the strengths and limitations of AI-generated insights, enabling informed decision-making." }
          ].map(({ title, description }) => (
            <div key={title} className="container textbox">
              <h4>{title}</h4>
              <p>{description}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="reliability-section">
        {[
          {
            title: "Pillar 1: Fidelity",
            question: "Does the synthetic data look like real data?",
            description: "Fidelity measures the statistical similarity between generated data and a real-world benchmark.",
            keyGaps: [
              "Lower Variability: AI responses often cluster around the mean, failing to capture the 'long tail' of outlier opinions.",
              "Nuance Deficit: Models struggle with complex emotional states and sarcasm.",
              "Inconsistent Logic: Personas may contradict themselves across multiple questions."
            ],
            chartTitle: "Visualizing the 'Average' Bias"
          },
          {
            title: "Pillar 2: Utility",
            question: "Does the synthetic data work like real data?",
            description: "Utility measures if the data is useful for practical tasks.",
            keyGaps: [
              "Train-Synthetic-Test-Real (TSTR): A model trained on synthetic data but tested on real data.",
              "LLM-as-a-Judge: Using another LLM to score responses on relevancy and faithfulness.",
              "Thematic Alignment: Comparing insights from synthetic analysis to real human data."
            ]
          },
          {
            title: "Pillar 3: Fairness & Privacy",
            question: "Is the synthetic data safe and fair to use?",
            description: "This ethical pillar assesses whether the data perpetuates harmful biases.",
            keyGaps: [
              "Membership Inference Attack: Testing if a model can determine if a specific individual's data was part of the training.",
              "Bias Measurement: Quantifying demographic and 'average' bias.",
              "De-biasing Techniques: Identifying and correcting potential biases during generation."
            ]
          }
        ].map(({ title, question, description, keyGaps, chartTitle }) => (
          <div key={title} className="container">
            <h3>{title}</h3>
            <p>{question}</p>
            <p>{description}</p>
            {keyGaps && (
              <div>
                <h4>Key Gaps</h4>
                <ul>
                  {keyGaps.map((gap, index) => (
                    <li key={index}>{gap}</li>
                  ))}
                </ul>
              </div>
            )}
            {chartTitle && (
              <div className="chart-container" style={{ height: 300 }}>
                <h4>{chartTitle}</h4>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={varianceData}>
                    <XAxis dataKey="name" />
                    <YAxis label={{ value: 'Variance (SD)', angle: -90, position: 'insideLeft' }} />
                    <Tooltip formatter={(value, name, props) => [value, `${props.payload.name}: ${props.payload.description}`]} />
                    <Bar dataKey="value" name="Response Variance" fill="#3b82f6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        ))}
      </div>

      <h2>Limitations We're Transparent About</h2>
      <p>While our technology provides valuable insights, we believe in being transparent about its limitations:</p>
      {[
        "Lower Statistical Variance: AI-generated responses tend to show less variance than human responses.",
        "Psychographic Depth: Current personas are defined primarily by demographic characteristics.",
        "Response Consistency: Personas may provide inconsistent answers across different questions.",
        "LLM Knowledge Limitations: Our AI's knowledge has a cutoff date and may not reflect very recent events."
      ].map((limitation, index) => (
        <div key={index} className="container textbox">
        <p>{limitation}</p>
      </div>
      ))}

      <h2>The Simulation Fidelity Spectrum</h2>
      <p>The maturity of synthetic persona technology can be seen as a spectrum of increasing fidelity.</p>
      <div className="content-grid-4">
        {[
          { level: 1, title: "Basic Role-Play", description: "Simple prompting of an LLM to act as different personas." },
          { level: 2, title: "Demographic Grounding", description: "Personas created from real-world demographic data." },
          { level: 3, title: "Psychographic Richness", description: "Personas enhanced with deep psychological profiles." },
          { level: 4, title: "Agent-Based Models", description: "Fully autonomous agents with memory and learning capabilities." }
        ].map(({ level, title, description }) => (
          <div key={level} className="container textbox">
            <h4>Level {level}: {title}</h4>
            <p>{description}</p>
          </div>
        ))}
      </div>

      <h2>When to Use InstantFlows vs. Traditional Research</h2>
      <div className="content-grid-1">
        <div className="textbox">
          <h3>InstantFlows is ideal for:</h3>
          <ul>
            <li>Early-stage concept testing and validation</li>
            <li>Message and campaign refinement</li>
            <li>Preliminary audience segmentation</li>
            <li>Exploring ideas before investing in larger studies</li>
            <li>Situations where speed and cost efficiency are critical</li>
          </ul>
        </div>
        
        <div className="textbox">
          <h3>Traditional research is better for:</h3>
          <ul>
            <li>Final validation of critical business decisions</li>
            <li>Research requiring deep psychological insights</li>
            <li>Highly specialized or technical topics</li>
            <li>Regulatory or compliance-related research</li>
          </ul>
        </div>
      </div>

      <h2>Our Commitment to Improvement</h2>
      <p>We're constantly enhancing our platform based on user feedback and technological advancements. Current areas of development include:</p>
      
      <div className="content-grid-3">
        {[
          { title: "Enhanced Persona Depth", description: "Adding richer psychographic attributes and behavioral patterns." },
          { title: "Improved Response Consistency", description: "Ensuring personas maintain coherent viewpoints across questions." },
          { title: "Better Analysis Tools", description: "Developing more sophisticated clustering and pattern recognition." }
        ].map(({ title, description }) => (
          <div key={title} className="textbox card">
            <h4>{title}</h4>
            <p>{description}</p>
          </div>
        ))}
      </div>

      <h2>The Future: A Hybrid Approach</h2>
      <p>We believe the future of market research lies in hybrid approaches—using AI-generated insights for rapid iteration and exploration, followed by targeted traditional research for validation.</p>
      
      <p>By being transparent about our capabilities and limitations, we hope to set appropriate expectations and help you get the most value from our platform.</p>
      
      <div className="mt-8 p-6 bg-blue-50 rounded-lg border border-blue-100 text-center">
        <p className="text-lg font-semibold mb-2">Want to learn more about the technical details?</p>
        <a href="/docs/AI Market Research Analysis.pdf" className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition-colors">Download our research report</a>
        <p className="mt-2 text-sm text-blue-600">For an in-depth analysis of AI-powered market research methodology</p>
      </div>
    </div>
  );
}